import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { OktaService } from './okta.service';
import { environment } from '../../../environments/environment';
import { HcToastOptions, HcToasterService } from '@healthcatalyst/cashmere';
import { ChangeElement, ChangeService } from './change.service';

export interface TermElement {
    'id': number,
    'name': string,
    'usage': string,
    'types': string,
    'categories': string,
    'example': string,
    'dateAdded': Date | null
}

@Injectable()
export class TerminologyService {
    termList:BehaviorSubject<TermElement[] | null> = new BehaviorSubject<TermElement[] | null>(null);
    loadFailed = false;

    constructor( 
        private http: HttpClient,
        private oktaService: OktaService,
        private toasterService: HcToasterService,
        private changeService: ChangeService 
    ){};

    get termsUpdated(): Observable<TermElement[] | null> {
        return this.termList.asObservable();
    }

    refresh() {
        this.loadFailed = false;

        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        const options = {headers: headers};

        this.http.get<any>( environment.azure.url + '/terms', options)
            .subscribe({
                next: data => {
                    let loadedTerms: TermElement[] = [];
                    data.forEach((term:any) => {
                        loadedTerms.push({
                            id: term.termID,
                            name: term.name,
                            usage: term.usage,
                            types: term.types,
                            categories: term.categories,
                            example: term.example,
                            dateAdded: term.dateAdded ? new Date(term.dateAdded) : null
                        });
                    });
                    this.termList.next ( loadedTerms );
                    this.loadFailed = false;
                },
                error: msg => { 
                    this.loadFailed = true;
                    this.termList.next(null);
                    console.log( msg );
                }
            });
    }

    add( target: TermElement, change: ChangeElement ) {
        // Write target to database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        let postSub = this.http.post<any>( environment.azure.url + '/admin.terms.add',
            {
                name: target.name,
                usage: target.usage,
                types: target.types,
                categories: target.categories,
                example: target.example,
                dateAdded: target.dateAdded ? target.dateAdded.toISOString() : null
            },
            options
        )
        .subscribe({
            next: targetId => {
                this.refresh();
                change.item = Number(targetId);
                this.changeService.add( [change] );
                const options: HcToastOptions = {
                    header: 'Term Added',
                    body: target.name + " successfully added.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Add Failed',
                    body: "Unable to add term. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log( msg );
                postSub.unsubscribe();
            }
        });
    }

    remove( target: TermElement ) {
        // Remove target from database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        let postSub = this.http.post<any>( environment.azure.url + '/admin.terms.remove',
            {
                termID: target.id
            },
            options
        )
        .subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'Term Removed',
                    body: target.name + " successfully removed.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Remove Failed',
                    body: "Unable to remove term. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log( msg );
                postSub.unsubscribe();
            }
        });
    }

    update( target: TermElement ) {
        // Update target in database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        let postSub = this.http.post<any>( environment.azure.url + '/admin.terms.update',
            {
                termID: target.id,
                name: target.name,
                usage: target.usage,
                types: target.types,
                categories: target.categories,
                example: target.example,
                dateAdded: target.dateAdded ? target.dateAdded.toISOString() : null
            },
            options
        )
        .subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'Term Updated',
                    body: target.name + " successfully updated.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Update Failed',
                    body: "Unable to update term. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log( msg );
                postSub.unsubscribe();
            }
        });
    }
}
